import styled from 'styled-components';
import colours from 'theme/atoms/colours';
import breakpoints from 'theme/atoms/breakpoints';

type ButtonSize = 'large' | 'medium' | 'small' | 'tiny';

const sizeConfig = {
  large: {
    minHeight: '60px',
    padding: '20px 25px',
    iconOnlyPadding: '20px',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: 'normal',
    iconSize: '20px',
  },
  medium: {
    minHeight: '50px',
    padding: '15px 20px',
    iconOnlyPadding: '15px',
    gap: '7px',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: 'normal',
    iconSize: '20px',
  },
  small: {
    minHeight: '40px',
    padding: '10px 15px',
    iconOnlyPadding: '10px',
    gap: '5px',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: 'normal',
    iconSize: '20px',
  },
  tiny: {
    minHeight: '30px',
    padding: '6px 10px',
    iconOnlyPadding: '6px',
    gap: '5px',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: 'normal',
    iconSize: '18px',
  },
} as const;

const getSize = (props: {
  $size?: ButtonSize;
  small?: boolean;
  medium?: boolean;
  tiny?: boolean;
}): ButtonSize => {
  if (props.$size) return props.$size;
  if (props.tiny) return 'tiny';
  if (props.small) return 'small';
  if (props.medium) return 'medium';
  return 'large';
};

export const Button = styled('button')`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  border-radius: 300px;
  box-shadow: none;
  box-sizing: border-box;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: ${(props) => sizeConfig[getSize(props)].fontSize};
  font-weight: ${(props) => sizeConfig[getSize(props)].fontWeight};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  justify-content: center;
  gap: ${(props) => sizeConfig[getSize(props)].gap};
  letter-spacing: ${(props) => sizeConfig[getSize(props)].letterSpacing};
  line-height: ${(props) => sizeConfig[getSize(props)].lineHeight};
  max-width: 600px;
  min-height: ${(props) => sizeConfig[getSize(props)].minHeight};
  padding: ${(props) => sizeConfig[getSize(props)].padding};
  text-decoration: none;
  width: ${(props) => (props.stretch ? '100%' : 'fit-content')};

  cursor: ${(props) => props.inactive && 'not-allowed'};
  opacity: ${(props) => props.inactive && '0.6'};
  pointer-events: ${(props) => props.inactive && 'none'};

  svg,
  img {
    height: ${(props) => sizeConfig[getSize(props)].iconSize};
    width: ${(props) => sizeConfig[getSize(props)].iconSize};
    stroke-width: 2;
  }

  svg path {
    stroke: white;
  }

  @media (max-width: ${breakpoints.tablet}) {
    flex-grow: 1;
    width: 100%;
  }
`;

export const Primary = styled(Button)`
  background-color: ${colours.primary[500]};
  border-radius: 300px;

  &:hover,
  &:focus {
    background-color: ${colours.primary[600]};
  }

  &:disabled {
    background-color: ${colours.primary[200]};
    color: white;
  }
`;

export const PrimaryIcon = styled(Primary)`
  align-items: center;
  flex-grow: 0;
  justify-content: center;
  padding: ${(props) => sizeConfig[getSize(props)].iconOnlyPadding};
  width: fit-content;

  img {
    filter: ${({ disabled }) => (disabled ? 'grayscale(100%)' : 'none')};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  }

  @media (max-width: ${breakpoints.tablet}) {
    flex-grow: 0;
    width: fit-content;
  }
`;

export const SecondaryLight = styled(Button)`
  background-color: ${colours.neutral[200]};
  backdrop-filter: blur(10px);
  border-radius: 100px;
  color: ${colours.neutral[900]};

  svg path {
    stroke: ${colours.neutral[900]};
  }

  &:hover,
  &:focus {
    background-color: ${colours.neutral[300]};
  }

  &:disabled {
    background-color: ${colours.neutral[200]};
    color: ${colours.neutral[400]};

    svg path {
      stroke: ${colours.neutral[400]};
    }
  }
`;

export const SecondaryLightIcon = styled(SecondaryLight)`
  align-items: center;
  flex-grow: 0;
  justify-content: center;
  padding: ${(props) => sizeConfig[getSize(props)].iconOnlyPadding};
  width: fit-content;

  @media (max-width: ${breakpoints.tablet}) {
    flex-grow: 0;
    width: fit-content;
  }
`;

export const SecondaryDark = styled(Button)`
  background-color: ${colours.neutral[900]};
  border-radius: 300px;
  color: white;

  svg path {
    stroke: white;
  }

  &:hover,
  &:focus {
    background-color: ${colours.shade[100]};
  }

  &:disabled {
    background-color: ${colours.neutral[300]};
    color: white;

    svg path {
      stroke: white;
    }
  }
`;

export const SecondaryDarkIcon = styled(SecondaryDark)`
  align-items: center;
  flex-grow: 0;
  justify-content: center;
  padding: ${(props) => sizeConfig[getSize(props)].iconOnlyPadding};
  width: fit-content;

  @media (max-width: ${breakpoints.tablet}) {
    flex-grow: 0;
    width: fit-content;
  }
`;

export const Ghost = styled(Button)`
  background-color: transparent;
  border-radius: 100px;
  color: ${colours.neutral[800]};

  svg path {
    stroke: ${colours.neutral[800]};
  }

  &:hover,
  &:focus {
    background-color: ${colours.neutral[200]};
  }

  &:disabled {
    background-color: transparent;
    color: ${colours.neutral[300]};

    svg path {
      stroke: ${colours.neutral[300]};
    }
  }
`;

export const GhostIcon = styled(Ghost)`
  align-items: center;
  flex-grow: 0;
  justify-content: center;
  padding: ${(props) => sizeConfig[getSize(props)].iconOnlyPadding};
  width: fit-content;

  @media (max-width: ${breakpoints.tablet}) {
    flex-grow: 0;
    width: fit-content;
  }
`;

export const SideBySide = styled('div')`
  display: flex;
  gap: 30px;
  width: 100%;

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column-reverse;
    gap: 15px;
  }
`;
